import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import ManualPicksPanel, {
    MONO_FONT, Swatch, formatLab, markedInLabel, plateAndExploration,
} from "../../components/ManualPicksPanel";
import useManualPicks from "../../helper/useManualPicks";
import {
    MANUAL_LOGO_SELECTION,
    isManualSelection,
    isSelectionFinalized,
    selectionResumeRoute,
    selectionSidebar,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";

/** Pause after the last keystroke before the search runs (ms). */
export const SEARCH_DEBOUNCE_MS = 250;

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

/**
 * ``[before, match, after]`` of ``sampleId`` around ``query``, or null.
 *
 * Matches ignore dashes and case like the backend does, so a fragment typed
 * without dashes ("f1c41234") is found across the dash of "…f1c4-1234…" and
 * the highlight covers exactly the matched characters.
 */
export function splitAtMatch(sampleId, query) {
    const id = String(sampleId || "");
    const wanted = String(query || "").replace(/-/g, "").trim().toLowerCase();
    if (!wanted) return null;
    const positions = [];
    let flat = "";
    for (let i = 0; i < id.length; i += 1) {
        if (id[i] !== "-") {
            positions.push(i);
            flat += id[i].toLowerCase();
        }
    }
    const at = flat.indexOf(wanted);
    if (at < 0) return null;
    const start = positions[at];
    const end = positions[at + wanted.length - 1] + 1;
    return [id.slice(0, start), id.slice(start, end), id.slice(end)];
}

function HighlightedId({ sampleId, query }) {
    const parts = splitAtMatch(sampleId, query);
    if (!parts) return <>{sampleId}</>;
    return (
        <>
            {parts[0]}
            <Box component="mark" sx={{ bgcolor: "#fde68a", color: "inherit", borderRadius: 0.5, px: 0.25 }}>
                {parts[1]}
            </Box>
            {parts[2]}
        </>
    );
}

/** One-line state of the result list. */
function resultSummary({ query, searching, total, shown }) {
    if (!query) return "";
    if (searching) return "Searching…";
    if (total === 0) return "No sample of this selection's explorations matches.";
    if (total > shown) return `${shown} of ${total} matches shown. Type more of the ID to narrow them down.`;
    return `${total} match${total === 1 ? "" : "es"}`;
}

/**
 * Manual logo selection, "Find by Sample ID": add samples by typing part of
 * their ID. Any fragment of the ID is enough (dashes optional); the matches
 * come from the explorations of the selection only. Next opens the Prepass
 * step, where the selection is finished.
 */
export default function ColorSelectionManualSearch() {
    const navigate = useNavigate();
    const { selectionId, stepRoute } = useSelectionParams();

    const [ready, setReady] = useState(false);
    const [pageError, setPageError] = useState(null);
    const [query, setQuery] = useState("");
    const [searchedFor, setSearchedFor] = useState("");
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [searching, setSearching] = useState(false);
    const requestSeq = useRef(0);

    const palette = useManualPicks(ready ? selectionId : null);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`api/color/selection/${selectionId}/`)
            .then((response) => {
                if (cancelled) return;
                const { status, selection_type: selectionType } = response.data || {};
                if (!isManualSelection(selectionType) || isSelectionFinalized(status)) {
                    navigate(selectionResumeRoute(selectionId, status, selectionType), { replace: true });
                    return;
                }
                setReady(true);
            })
            .catch((err) => {
                if (!cancelled) setPageError(errorText(err, "Loading the selection failed."));
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Search as the user types, once the typing pauses; only the answer to
    // the latest query is shown.
    useEffect(() => {
        if (!ready) return undefined;
        const trimmed = query.trim();
        const seq = ++requestSeq.current;
        if (!trimmed) {
            setResults([]);
            setTotal(0);
            setSearchedFor("");
            setSearching(false);
            return undefined;
        }
        setSearching(true);
        const timer = setTimeout(() => {
            axios
                .post("api/selection/manual/search", { selectionId, query: trimmed })
                .then((response) => {
                    if (seq !== requestSeq.current) return;
                    setResults(response.data.results || []);
                    setTotal(response.data.total || 0);
                    setSearchedFor(trimmed);
                })
                .catch((err) => {
                    if (seq !== requestSeq.current) return;
                    setResults([]);
                    setTotal(0);
                    setPageError(errorText(err, "Searching failed."));
                })
                .finally(() => {
                    if (seq === requestSeq.current) setSearching(false);
                });
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, ready]);

    const count = palette.picks.length;
    const summary = resultSummary({ query: query.trim(), searching, total, shown: results.length });

    return (
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title="Find by Sample ID"
                subtitle="Type part of a sample ID; click a match to add it to the palette."
                stepSidebar={selectionSidebar("search", count, MANUAL_LOGO_SELECTION)}
                footerBelowPaper={
                    <Box sx={{ width: "100%" }}>
                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={stepRoute("plates", MANUAL_LOGO_SELECTION)}
                            nextPage={stepRoute("prepass", MANUAL_LOGO_SELECTION)}
                            enableNextPage={!ready || count === 0}
                            nextPageClickFunc={(page) => navigate(page)}
                        />
                    </Box>
                }
            >
                {pageError ? (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPageError(null)}>
                        {pageError}
                    </Alert>
                ) : null}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        gap: 3,
                        alignItems: "flex-start",
                    }}
                >
                    <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Sample ID"
                            placeholder="e.g. 7b02f1c4"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            helperText="Any part of the ID; dashes are optional."
                            inputProps={{ spellCheck: false, autoComplete: "off", style: { fontFamily: MONO_FONT } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searching ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={18} />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />

                        {summary ? (
                            <Typography variant="body2" color="text.secondary" data-testid="search-summary">
                                {summary}
                            </Typography>
                        ) : null}

                        <Stack spacing={1} component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                            {results.map((row) => {
                                const picked = palette.isPicked(row.sample_id);
                                const pending = palette.isPending(row.sample_id);
                                // The pool only holds colour-measured samples; guard anyway.
                                const pickable = Array.isArray(row.lab) || picked;
                                return (
                                    <Box
                                        component="li"
                                        key={row.sample_id}
                                        data-testid="search-result"
                                        onClick={() => {
                                            if (!pending && pickable) palette.toggle(row.sample_id);
                                        }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            p: 1.25,
                                            borderRadius: 2,
                                            border: "1px solid",
                                            borderColor: picked ? "rgba(22, 163, 74, 0.45)" : "rgba(15, 23, 42, 0.1)",
                                            bgcolor: picked ? "rgba(22, 163, 74, 0.06)" : "background.paper",
                                            cursor: "pointer",
                                            transition: "border-color 0.15s ease, background-color 0.15s ease",
                                            "&:hover": { borderColor: picked ? "rgba(22, 163, 74, 0.7)" : "rgba(15, 23, 42, 0.28)" },
                                        }}
                                    >
                                        <Swatch rgb={row.rgb} size={36} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                sx={{ fontFamily: MONO_FONT, fontSize: 13.5, fontWeight: 600, wordBreak: "break-all" }}
                                            >
                                                <HighlightedId sampleId={row.sample_id} query={searchedFor} />
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                                                {[plateAndExploration(row), markedInLabel(row.marked_in)].filter(Boolean).join(" · ")}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block", lineHeight: 1.4, fontVariantNumeric: "tabular-nums" }}
                                            >
                                                {[
                                                    `L*a*b* ${formatLab(row.lab)}`,
                                                    row.width_mm !== null && row.width_mm !== undefined
                                                        ? `${(Number(row.width_mm) * 1000).toFixed(1)} µm`
                                                        : null,
                                                ].filter(Boolean).join(" · ")}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant={picked ? "contained" : "outlined"}
                                            color={picked ? "success" : "primary"}
                                            disableElevation
                                            disabled={pending || !pickable}
                                            startIcon={picked ? <CheckIcon /> : <AddIcon />}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                palette.toggle(row.sample_id);
                                            }}
                                            aria-label={`${picked ? "Remove" : "Add"} ${row.sample_id}`}
                                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, minWidth: 96, flexShrink: 0 }}
                                        >
                                            {picked ? "Added" : "Add"}
                                        </Button>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Stack>

                    <Box sx={{ width: { xs: "100%", lg: 300 }, flexShrink: 0 }}>
                        <ManualPicksPanel
                            selectionId={selectionId}
                            picks={palette.picks}
                            loading={palette.loading}
                            onRemove={(sampleId) => palette.remove([sampleId])}
                            onClear={palette.clear}
                        />
                    </Box>
                </Box>
            </ExplorationPageShell>

            <Snackbar
                open={Boolean(palette.error)}
                autoHideDuration={6000}
                onClose={palette.clearError}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error" onClose={palette.clearError} sx={{ width: "100%" }}>
                    {palette.error}
                </Alert>
            </Snackbar>
        </Stack>
    );
}
